"use client";

import { usePathname } from "next/navigation";
import { AuthEvent } from "./AuthEvent";
import { SponsoredTeasers } from "./SponsoredTeasers";
import { useEffect, useState } from "react";
import { CONSENT_CHANGED_EVENT, readConsent } from "@/lib/analytics/consent";
import { connectAnalytics, disconnectAnalytics } from "@/lib/analytics/events";
import {
  GA_CONSENT_DEFAULTS,
  GA_SCRIPT_URL,
  gaCookieDomains,
  isGaCookie,
} from "@/lib/analytics/ga";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

// One tag per page load, like the bundle it loads: gtag.js keeps its state in
// the window, so a remount (locale switch) must not configure it twice.
let loaded = false;

function gtag(...args: unknown[]) {
  window.gtag?.(...args);
}

// The theme's catalog events (lib/analytics/events.ts) go out as GA events —
// after `config`, so a held event never reaches GA ahead of its page.
function connectEvents() {
  connectAnalytics((event, params) => gtag("event", event, params));
}

function start(id: string) {
  // GA's documented opt-out switch; cleared again when consent comes back.
  (window as unknown as Record<string, boolean>)[`ga-disable-${id}`] = false;
  if (loaded) {
    gtag("consent", "update", { analytics_storage: "granted" });
    connectEvents();
    return;
  }
  loaded = true;
  window.dataLayer = window.dataLayer ?? [];
  // gtag.js reads `arguments` objects off the dataLayer, not arrays.
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };
  gtag("consent", "default", GA_CONSENT_DEFAULTS);
  gtag("consent", "update", { analytics_storage: "granted" });
  gtag("js", new Date());
  gtag("config", id);
  connectEvents();
  const script = document.createElement("script");
  script.async = true;
  script.src = `${GA_SCRIPT_URL}?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
}

function stop(id: string) {
  disconnectAnalytics();
  (window as unknown as Record<string, boolean>)[`ga-disable-${id}`] = true;
  gtag("consent", "update", { analytics_storage: "denied" });
  // Withdrawal removes what acceptance wrote (GDPR: as easy to take back).
  for (const part of document.cookie.split(";")) {
    const name = part.split("=")[0]?.trim() ?? "";
    if (!isGaCookie(name)) continue;
    for (const domain of gaCookieDomains(window.location.hostname)) {
      document.cookie = `${name}=;path=/;domain=${domain};max-age=0`;
    }
    document.cookie = `${name}=;path=/;max-age=0`;
  }
}

/**
 * GA4 for a portal whose record names a measurement ID (lib/analytics/ga.ts
 * has the rules). Starts on the stored "granted" choice or the banner's
 * accept, stops on a withdrawal from the cookies page — the consent banner is
 * the one shared with PostHog. A CMS preview (`/<lang>/preview/<token>`) never
 * loads it: GA's page views carry the full URL, and the token is a credential
 * (lib/analytics/scrub-url.ts) — nothing on the site links into a preview, so
 * the page a visitor lands on decides.
 */
export function GoogleAnalytics({ measurementId }: { measurementId: string }) {
  // Decided by the page the visitor lands on, once.
  const pathname = usePathname();
  const [preview] = useState(() => /\/preview\//.test(pathname ?? ""));

  useEffect(() => {
    if (preview) return;
    const apply = () => {
      if (readConsent() === "granted") start(measurementId);
      else if (loaded) stop(measurementId);
    };
    apply();
    window.addEventListener(CONSENT_CHANGED_EVENT, apply);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, apply);
  }, [measurementId, preview]);

  if (preview) return null;
  return (
    <>
      <SponsoredTeasers />
      <AuthEvent />
    </>
  );
}
